/**
 * Gesture Utilities
 *
 * Helper functions and constants for gesture-based interactions
 */

/**
 * Determines if a swipe gesture should trigger a minimize action
 * @param delta - The movement delta from gesture
 * @param velocity - The velocity of the gesture
 * @returns true if the gesture should trigger minimize
 */
export function shouldMinimize(delta: number, velocity: number): boolean {
	// Minimum distance to trigger (in pixels)
	const SWIPE_THRESHOLD = 100;
	// Velocity threshold (pixels per millisecond)
	const VELOCITY_THRESHOLD = 0.5;

	return (
		Math.abs(delta) > SWIPE_THRESHOLD || Math.abs(velocity) > VELOCITY_THRESHOLD
	);
}

/**
 * Determines if a swipe gesture should trigger an expand action
 * @param delta - The movement delta from gesture
 * @param velocity - The velocity of the gesture
 * @returns true if the gesture should trigger expand
 */
export function shouldExpand(delta: number, velocity: number): boolean {
	const SWIPE_THRESHOLD = 80;
	const VELOCITY_THRESHOLD = 0.5;

	return (
		Math.abs(delta) > SWIPE_THRESHOLD || Math.abs(velocity) > VELOCITY_THRESHOLD
	);
}

/**
 * Clamps a value between min and max
 */
export function clamp(value: number, min: number, max: number): number {
	return Math.min(Math.max(value, min), max);
}

/**
 * Provides haptic feedback for supported devices
 */
export function hapticFeedback(
	type: "light" | "medium" | "heavy" = "medium",
): void {
	if ("vibrate" in navigator) {
		const patterns = {
			light: 10,
			medium: 20,
			heavy: 30,
		};
		navigator.vibrate(patterns[type]);
	}
}

/**
 * Calculates the rubberband effect for overscroll
 * Makes scrolling feel more natural at boundaries
 */
export function rubberband(
	distance: number,
	dimension: number,
	constant = 0.15,
): number {
	return (
		(distance * dimension * constant) /
		(dimension + constant * Math.abs(distance))
	);
}
